import { ActionConfirmationModal, Typography } from "@/components/ui";
import type { PossibleDuplicateContact } from "@/lib/data/requests";
import { formatContactsName } from "@/lib/utils/format";

type PossibleDuplicateContactModalProps = {
  candidates: PossibleDuplicateContact[];
  onCancel: () => void;
  onCreateAnyway: () => void | Promise<void>;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const formatMatchReasons = (contact: PossibleDuplicateContact): string =>
  contact.matchedOn
    .map(
      (reason) =>
        ({
          exactName: "exact name",
          similarName: "similar name",
          email: "email",
          phone: "phone",
          address: "address",
        })[reason],
    )
    .join(", ");

export function PossibleDuplicateContactModal({
  candidates,
  onCancel,
  onCreateAnyway,
  open,
  setOpen,
}: PossibleDuplicateContactModalProps) {
  return (
    <ActionConfirmationModal
      open={open}
      setOpen={setOpen}
      title="Possible duplicate contact"
      description={
        <div className="space-y-3">
          <Typography variant="subHeading">
            A possible duplicate contact already exists. Are you sure you want
            to continue?
          </Typography>
          <div>
            <Typography variant="default" className="font-semibold">
              Matching contacts
            </Typography>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {candidates.slice(0, 5).map((contact) => (
                <li key={contact.id}>
                  <a
                    href={`/contacts/${contact.id}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-primary underline"
                  >
                    {formatContactsName(contact) ?? "Contact"}
                  </a>
                  <span className="text-muted-foreground">
                    {` (${contact.type}; matched on ${formatMatchReasons(contact)})`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
      buttonText="Create anyway"
      cancelText="Go back"
      actionVariant="default"
      afterActionFn={onCreateAnyway}
      onCancel={onCancel}
    />
  );
}
